import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';
import {
  Plus, Ship, Map, Compass, PartyPopper, Car, Hotel, MapPin, Clock, Users,
  ChevronRight, ShoppingBag, MessageCircle, Sparkles, Loader2,
} from 'lucide-react';

const CATEGORY_ICONS = {
  yacht: Ship, tour: Map, activity: Compass,
  party: PartyPopper, car_rental: Car, lodging: Hotel,
};

const BIG_CATEGORIES = [
  { id: 'tours', icon: Map, label_en: 'Tours', label_es: 'Tours',
    image: 'https://images.unsplash.com/photo-1766776341444-f8f5c95693cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjZW5vdGUlMjBzd2ltbWluZyUyMG1leGljbyUyMGp1bmdsZSUyMHdhdGVyfGVufDB8fHx8MTc3NDAyMTM5Nnww&ixlib=rb-4.1.0&q=85' },
  { id: 'yachts', icon: Ship, label_en: 'Yachts', label_es: 'Yates',
    image: 'https://images.pexels.com/photos/5046521/pexels-photo-5046521.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { id: 'nightlife', icon: PartyPopper, label_en: 'Nightlife', label_es: 'Vida nocturna',
    image: 'https://images.unsplash.com/photo-1746003625448-d8adecc74050?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHx0dWx1bSUyMG5pZ2h0bGlmZSUyMHBhcnR5JTIwYm9obyUyMGNoaWN8ZW58MHx8fHwxNzc0MDIxMzk4fDA&ixlib=rb-4.1.0&q=85' },
  { id: 'activities', icon: Compass, label_en: 'Activities', label_es: 'Actividades',
    image: 'https://images.unsplash.com/photo-1518182170546-07661fd94144?auto=format&fit=crop&q=80&w=600' },
  { id: 'lodging', icon: Hotel, label_en: 'Lodging', label_es: 'Hospedaje',
    image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&q=80&w=600' },
  { id: 'car_rental', icon: Car, label_en: 'Car rental', label_es: 'Renta de autos',
    image: 'https://images.unsplash.com/photo-1502877338535-766e1452684a?auto=format&fit=crop&q=80&w=600' },
];

export default function TouristDashboard() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();

  const [requests, setRequests] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'bookings' ? 'bookings' : 'requests');

  const fetchData = useCallback(async () => {
    try {
      const [reqRes, bookRes] = await Promise.all([
        api().get('/requests'),
        api().get('/bookings'),
      ]);
      setRequests(reqRes.data);
      setBookings(bookRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'bookings' || t === 'requests') setActiveTab(t);
  }, [searchParams]);

  const getCategoryName = (id) => {
    const names = {
      yacht: language === 'en' ? 'Yacht' : 'Yate',
      tour: 'Tour',
      activity: language === 'en' ? 'Activity' : 'Actividad',
      party: language === 'en' ? 'Party' : 'Fiesta',
      car_rental: language === 'en' ? 'Car Rental' : 'Renta de Auto',
      lodging: language === 'en' ? 'Lodging' : 'Hospedaje',
    };
    return names[id] || id;
  };

  const getLocationName = (id) => {
    const names = {
      playa_del_carmen: 'Playa del Carmen', cancun: 'Cancún',
      tulum: 'Tulum', quintana_roo: 'Quintana Roo', los_cabos: 'Los Cabos',
    };
    return names[id] || id;
  };

  const getStatusColor = (status) => ({
    active: 'bg-caribbean text-white',
    booked: 'bg-gold text-foreground',
    expired: 'bg-muted text-muted-foreground',
    cancelled: 'bg-destructive text-white',
    pending_payment: 'bg-rosa text-white',
    partially_paid: 'bg-gold text-foreground',
    confirmed: 'bg-caribbean text-white',
    completed: 'bg-foreground text-white',
  }[status] || 'bg-muted');

  if (loading) {
    return (
      <MobileShell>
        <TopBar showBrand title={language === 'en' ? 'Home' : 'Inicio'} />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="animate-spin text-caribbean" />
        </div>
      </MobileShell>
    );
  }

  const firstName = user?.name?.split(' ')[0] || '';

  return (
    <MobileShell>
      <TopBar showBrand />

      {/* Welcome */}
      <section className="px-4 pt-4" data-testid="tourist-welcome">
        <p className="text-xs text-muted-foreground">
          {language === 'en' ? 'Welcome back' : 'Bienvenido'}
        </p>
        <h1 className="font-heading text-2xl font-bold leading-tight">
          {language === 'en' ? `Hey, ${firstName}!` : `¡Hola, ${firstName}!`}
        </h1>
        <p className="text-sm text-muted-foreground">
          {language === 'en' ? 'What adventure are you planning today?' : '¿Qué aventura planeas hoy?'}
        </p>
      </section>

      {/* New search CTA */}
      <Link to="/dashboard/create-request" data-testid="new-request-cta">
        <div className="mx-4 mt-4 rounded-3xl bg-gradient-to-br from-caribbean to-caribbean/80 p-5 shadow-xl active:scale-[0.98] transition-transform">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center text-white">
              <Sparkles size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-white font-bold text-lg leading-tight">
                {language === 'en' ? 'Find your ideal experience' : 'Encuentra tu experiencia ideal'}
              </p>
              <p className="text-white/80 text-xs">
                {language === 'en' ? 'Tell us your budget — providers compete in 12h' : 'Cuéntanos tu presupuesto — proveedores compiten en 12h'}
              </p>
            </div>
            <ChevronRight className="text-white shrink-0" size={22} />
          </div>
        </div>
      </Link>

      {/* Chuy CTA mini -> opens chat */}
      <Link to="/chuy-chat" data-testid="chuy-cta-card">
        <div className="mx-4 mt-3 rounded-2xl bg-card border border-border p-3 flex items-center gap-3 active:scale-[0.98] transition-transform">
          <img src="/chuy.png" alt="Chuy" className="w-10 h-10 rounded-full object-cover border-2 border-caribbean" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground">Chuy · 24/7</p>
            <p className="text-sm font-semibold truncate">
              {language === 'en' ? "Don't know where to go? Ask me" : '¿No sabes a dónde ir? Pregúntame'}
            </p>
          </div>
          <MessageCircle className="text-caribbean shrink-0" size={18} />
        </div>
      </Link>

      {/* Categories grid - 6 big cards */}
      <section className="mt-6" data-testid="big-categories">
        <div className="flex items-center justify-between px-4 mb-3">
          <h2 className="font-heading font-bold text-base flex items-center gap-1.5">
            <ShoppingBag size={16} className="text-caribbean" />
            {language === 'en' ? 'Browse experiences' : 'Explora experiencias'}
          </h2>
          <Link to="/catalog" className="text-xs text-caribbean font-medium flex items-center gap-0.5">
            {language === 'en' ? 'See all' : 'Ver todo'} <ChevronRight size={12} />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 px-4">
          {BIG_CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.id}
                to={`/catalog?category=${c.id}`}
                data-testid={`big-cat-${c.id}`}
                className="group relative aspect-[4/5] rounded-2xl overflow-hidden shadow-md active:scale-95 transition-transform"
              >
                <img src={c.image} alt={c.label_en} className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                  <Icon size={16} className="text-white" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                  <p className="font-heading font-bold text-base leading-tight">
                    {language === 'en' ? c.label_en : c.label_es}
                  </p>
                  <p className="text-[10px] text-white/70 mt-0.5">
                    {language === 'en' ? 'Tap to explore' : 'Toca para ver'}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Tabs */}
      <section className="mt-6 px-4 pb-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full grid grid-cols-2 mb-3 bg-muted rounded-full h-10">
            <TabsTrigger value="requests" data-testid="requests-tab" className="rounded-full text-xs">
              {language === 'en' ? 'My searches' : 'Mis búsquedas'} ({requests.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="bookings-tab" className="rounded-full text-xs">
              {language === 'en' ? 'My bookings' : 'Mis reservas'} ({bookings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="space-y-2.5 mt-3">
            {requests.length === 0 ? (
              <div className="text-center py-10 border-2 border-dashed border-border rounded-2xl">
                <Sparkles className="mx-auto text-caribbean mb-2" />
                <p className="text-sm text-muted-foreground">
                  {language === 'en' ? 'No searches yet' : 'Sin búsquedas aún'}
                </p>
                <Link to="/dashboard/create-request" className="text-xs text-caribbean font-medium">
                  {language === 'en' ? 'Create your first one →' : 'Haz la primera →'}
                </Link>
              </div>
            ) : (
              requests.map((req) => {
                const Icon = CATEGORY_ICONS[req.category] || Compass;
                const budgetText = req.budget_min || req.budget_max
                  ? `$${req.budget_min || 0}–${req.budget_max || 0}`
                  : (req.suggested_avg ? `~$${Math.round(req.suggested_avg)}` : '');
                return (
                  <Link
                    key={req.id}
                    to={`/dashboard/requests/${req.id}`}
                    data-testid={`request-card-${req.id}`}
                    className="block bg-card border border-border rounded-2xl p-3.5 active:scale-[0.99] transition-transform"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-10 h-10 bg-caribbean/10 rounded-xl flex items-center justify-center shrink-0">
                          <Icon className="text-caribbean" size={18} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm capitalize truncate">
                            {req.subcategory_name || getCategoryName(req.category)}
                          </p>
                          <p className="text-[11px] text-muted-foreground flex items-center gap-0.5 truncate">
                            <MapPin size={11} /> {getLocationName(req.location)}
                          </p>
                        </div>
                      </div>
                      <Badge className={`${getStatusColor(req.status)} text-[10px] shrink-0`}>
                        {req.status === 'active' && (language === 'en' ? 'Active' : 'Activa')}
                        {req.status === 'booked' && (language === 'en' ? 'Booked' : 'Reservada')}
                        {req.status === 'expired' && (language === 'en' ? 'Expired' : 'Expirada')}
                        {req.status === 'cancelled' && (language === 'en' ? 'Cancelled' : 'Cancelada')}
                      </Badge>
                    </div>

                    {req.notes && (
                      <p className="text-[11px] text-muted-foreground line-clamp-2 mb-2 italic">"{req.notes}"</p>
                    )}

                    <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-2">
                      <span className="inline-flex items-center gap-1">
                        <Clock size={11} /> {req.date}{req.time ? ` · ${req.time}` : ''}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Users size={11} /> {req.people_count}
                      </span>
                      {budgetText && (
                        <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                          {budgetText}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[11px] text-caribbean font-semibold">
                        {req.offer_count || 0} {language === 'en' ? 'offers received' : 'ofertas recibidas'}
                      </span>
                      <ChevronRight size={14} className="text-muted-foreground" />
                    </div>
                  </Link>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="bookings" className="space-y-2.5 mt-3">
            {bookings.length === 0 ? (
              <div className="text-center py-10 border-2 border-dashed border-border rounded-2xl">
                <ShoppingBag className="mx-auto text-caribbean mb-2" />
                <p className="text-sm text-muted-foreground">
                  {language === 'en' ? 'No bookings yet' : 'Sin reservas aún'}
                </p>
                <Link to="/catalog" className="text-xs text-caribbean font-medium">
                  {language === 'en' ? 'Browse catalog →' : 'Ver catálogo →'}
                </Link>
              </div>
            ) : (
              bookings.map((b) => {
                const Icon = CATEGORY_ICONS[b.category] || Compass;
                return (
                  <Link
                    key={b.id}
                    to={`/dashboard/bookings/${b.id}`}
                    data-testid={`booking-card-${b.id}`}
                    className="block bg-card border border-border rounded-2xl overflow-hidden shadow-sm active:scale-[0.99] transition-transform"
                  >
                    <div className="flex items-stretch">
                      {/* Photo or icon column */}
                      <div className="w-20 shrink-0 bg-gradient-to-br from-caribbean to-rosa relative">
                        {b.product_photo ? (
                          <img src={b.product_photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-white/60">
                            <Icon size={28} />
                          </div>
                        )}
                      </div>
                      {/* Content */}
                      <div className="flex-1 min-w-0 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-sm leading-tight line-clamp-2">
                            {b.product_title || getCategoryName(b.category)}
                          </p>
                          <Badge className={`${getStatusColor(b.status)} text-[9px] shrink-0`}>
                            {b.status === 'pending_payment' && (language === 'en' ? 'Pending' : 'Pendiente')}
                            {b.status === 'partially_paid' && (language === 'en' ? 'Deposit' : 'Apartado')}
                            {b.status === 'confirmed' && (language === 'en' ? 'Confirmed' : 'Confirmado')}
                            {b.status === 'completed' && (language === 'en' ? 'Done' : 'Listo')}
                            {b.status === 'cancelled' && (language === 'en' ? 'Cancelled' : 'Cancelado')}
                          </Badge>
                        </div>
                        <p className="text-[11px] text-muted-foreground flex items-center gap-0.5 mt-0.5 truncate">
                          <MapPin size={11} /> {getLocationName(b.location)}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5 text-[11px] text-muted-foreground">
                          {b.date && (
                            <span className="inline-flex items-center gap-0.5">
                              <Clock size={11} /> {b.date}{b.time ? ` · ${b.time}` : ''}
                            </span>
                          )}
                          {b.people_count && (
                            <span className="inline-flex items-center gap-0.5">
                              <Users size={11} /> {b.people_count}
                            </span>
                          )}
                        </div>
                        <div className="flex items-baseline justify-between mt-1.5 pt-1.5 border-t border-border">
                          <span className="text-[10px] text-muted-foreground">
                            {language === 'en' ? 'Total' : 'Total'} ${b.total_price}
                          </span>
                          <span className="text-[11px] font-heading font-bold text-caribbean">
                            ${(b.reservation_fee ?? b.total_price)?.toFixed?.(2) ?? b.total_price} <span className="font-normal text-[9px]">{language === 'en' ? 'reserved' : 'reservé'}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </section>
    </MobileShell>
  );
}
